import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import { 
  Heart, 
  Trash2, 
  Building2,
  MapPin,
  Clock,
  ExternalLink,
  Search,
  Briefcase,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.user_type !== 'professional') {
      navigate('/dashboard');
      return;
    }
    fetchFavorites();
  }, [user, navigate]);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavorites(res.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      if (error.response?.status !== 403) {
        toast.error('Errore nel caricamento dei preferiti');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (listingId) => {
    try {
      await axios.delete(`${API}/favorites/${listingId}`);
      setFavorites(favorites.filter(f => f.listing_id !== listingId));
      toast.success('Rimosso dai preferiti');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const filteredFavorites = favorites.filter(fav => {
    const listing = fav.listing;
    if (!listing) return false;
    const search = searchQuery.toLowerCase();
    return listing.title?.toLowerCase().includes(search) ||
           listing.category?.toLowerCase().includes(search) ||
           listing.company_name?.toLowerCase().includes(search) ||
           listing.location_city?.toLowerCase().includes(search);
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta': return 'bg-red-100 text-red-700';
      case 'media': return 'bg-yellow-100 text-yellow-700';
      case 'bassa': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-green-600' : 'text-slate-400';
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#344B71]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 text-red-400 fill-red-400" />
            <h1 className="text-3xl font-playfair font-bold text-[#344B71]">
              Annunci Salvati
            </h1>
          </div>
          <p className="text-slate-600">
            Gli annunci che hai salvato per monitorarli facilmente
          </p>
        </motion.div>

        {/* Search */}
        {favorites.length > 0 && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Cerca tra i preferiti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-[#344B71]"
              />
            </div>
          </div>
        )}

        {/* Favorites Grid */}
        {filteredFavorites.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFavorites.map((favorite, index) => {
              const listing = favorite.listing;
              if (!listing) return null;
              
              return (
                <motion.div
                  key={favorite.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 bg-white hover:shadow-lg transition-shadow relative">
                    {/* Remove Button */}
                    <button
                      onClick={() => removeFavorite(favorite.listing_id)}
                      className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition-colors"
                      title="Rimuovi dai preferiti"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>

                    {/* Status Badge */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(listing.priority)}`}>
                        {listing.priority === 'alta' ? 'Alta Priorità' : 
                         listing.priority === 'media' ? 'Media Priorità' : 'Bassa Priorità'}
                      </span>
                      {listing.status !== 'active' && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Chiuso
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="font-semibold text-[#344B71] text-lg mb-2 pr-8 line-clamp-2">
                      {listing.title}
                    </h3>

                    {/* Company */}
                    <div className="flex items-center gap-2 mb-3">
                      {listing.company_logo ? (
                        <img src={listing.company_logo} alt="" className="w-6 h-6 rounded object-contain" />
                      ) : (
                        <Building2 className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="text-sm text-slate-600">{listing.company_name}</span>
                    </div>

                    {/* Category & Location */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Briefcase className="w-4 h-4" />
                        <span>{listing.category}</span>
                      </div>
                      {(listing.location_city || listing.location_province) && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <MapPin className="w-4 h-4" />
                          <span>
                            {listing.location_city}{listing.location_city && listing.location_province ? ', ' : ''}{listing.location_province}
                          </span>
                        </div>
                      )}
                      {listing.location_type && (
                        <span className="inline-block px-2 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-xs">
                          {listing.location_type === 'remote' ? 'Remoto' : 
                           listing.location_type === 'hybrid' ? 'Ibrido' : 'In sede'}
                        </span>
                      )}
                    </div>

                    {/* Saved Date */}
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                      <Clock className="w-3 h-3" />
                      <span>Salvato il {new Date(favorite.created_at).toLocaleDateString('it-IT')}</span>
                    </div>

                    {/* Notes */}
                    {favorite.notes && (
                      <p className="text-sm text-slate-500 italic mb-4 border-l-2 border-[#344B71] pl-3">
                        {favorite.notes}
                      </p>
                    )}

                    {/* Action Button */}
                    <Button
                      onClick={() => navigate(`/listings/${listing.id}`)}
                      className="w-full bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
                      disabled={listing.status !== 'active'}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Vedi Dettagli
                    </Button>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center bg-white">
            <Heart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#344B71] mb-2">
              {searchQuery ? 'Nessun risultato' : 'Nessun annuncio salvato'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery 
                ? 'Prova a modificare la ricerca'
                : 'Salva gli annunci che ti interessano per monitorarli facilmente'
              }
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate('/listings')}
                className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
              >
                Esplora Annunci
              </Button>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FavoritesPage;
